import { defineStore } from "pinia";
import api, { cloneMateTrait } from "@/modules/api";
import { useMate } from "@/stores/mate";
import { getOrSet } from "@/modules/cache";
import { showLoadingToast } from "vant";
import i18n from "@/locales";

const PER_SIZE = 999;

export const useMateTrait = defineStore("mateTrait", {
  state: () => {
    return {
      traitMap: {} as Record<number, MateTrait>,
      traitList: [] as MateTrait[],
      portraitList: [] as MatePortrait[],
      init: false,

      currentMateId: 0,
      mateIdTraitListMap: {} as Record<number, MateTrait[]>,
    };
  },
  actions: {
    showCreateTraitGuide: () => {
      const val = getOrSet("showCreateTraitGuide", false);
      return val === null ? true : val;
    },
    getOrFetchTraitItem: async function (id: number, mateId?: number) {
      if (this.traitMap[id]) return this.traitMap[id];
      const trait = this.traitList.find((trait) => trait.id === id);
      if (!trait) {
        const res = await api.readMateTraitPage({
          filter: { id },
        });
        this.traitMap[id] = res.page.data[0];
        return res.page.data[0];
      }
      this.traitMap[id] = trait;
      return trait;
    },
    getOrFetchTraitList: async function (force = false, mateId?: number) {
      const mateStore = useMate();
      if (!mateStore.currentMate?.id) return false;
      if (force || !this.init) {
        const res = await api.readMateTraitPage({
          pagination: {
            page: 1,
            perPage: PER_SIZE,
          },
          sort: {
            field: "id",
            order: "desc",
          },
          filter: {
            mateId: mateId || mateStore.currentMate?.id,
          },
        });
        this.traitList = res.page.data;
        this.mateIdTraitListMap[mateId || mateStore.currentMate?.id!] =
          this.traitList;
        this.traitList.forEach((trait) => {
          this.traitMap[trait.id!] = trait;
        });
        this.init = true;
      }
    },
    getOrFetchTraitListByMateId: async function (
      mateId: number,
      force = false
    ) {
      if (this.mateIdTraitListMap[mateId] && !force)
        return this.mateIdTraitListMap[mateId];
      this.currentMateId = mateId;
      const res = await api.readMateTraitPage({
        pagination: {
          page: 1,
          perPage: PER_SIZE,
        },
        sort: {
          field: "id",
          order: "desc",
        },
        filter: {
          mateId,
        },
      });
      this.mateIdTraitListMap[mateId] = res.page.data;
      return res.page.data;
    },
    getOrFetchTraitListById: async function (mateId: number) {
      const res = await api.readMateTraitPage({
        pagination: {
          page: 1,
          perPage: PER_SIZE,
        },
        sort: {
          field: "id",
          order: "desc",
        },
        filter: {
          mateId,
        },
      });
      res.page.data.forEach((trait) => {
        this.traitMap[trait.id!] = trait;
      });
      return res.page.data;
    },
    async createTrait(params: {
      name: string;
      sourceId?: number;
      mediaId?: number;
      prompt: string;
      negativePrompt?: string;
      style: number;
      task?: boolean;
      mateId?: number;
    }): Promise<MateTrait> {
      let uuid = window.$router.currentRoute.value.query.uuid as string;
      if (uuid) {
        const mate = useMate().mateMap.get(uuid);
        if (mate) {
          params.mateId = mate.id;
        }
      }

      const res = await api.createOneMateTrait(params);
      this.traitList.unshift(res.item);
      this.mateIdTraitListMap[res.item.mateId!].unshift(res.item);
      return res.item;
    },
    async updateTrait(
      id: number,
      params: Partial<
        Pick<
          MateTrait,
          | "name"
          | "style"
          | "prompt"
          | "sourceId"
          | "negativePrompt"
          | "mediaId"
        >
      >
    ) {
      await api.updateOneMateTrait(id, params);
      await this.getOrFetchTraitList(true);
    },
    async cloneTrait(id: number) {
      await cloneMateTrait(id);
      await this.getOrFetchTraitList(true);
    },
    async deleteTrait(id: number) {
      await api.deleteOneMateTrait(id);
      this.traitList = this.traitList.filter((trait) => trait.id !== id);
      this.mateIdTraitListMap[this.currentMateId] = this.mateIdTraitListMap[
        this.currentMateId
      ].filter((trait) => trait.id !== id);
    },
    async createPortrait(params: MateTraitPortraitParams) {
      const res = await api.createOneMateTraitPortrait(params);
      return res.item;
    },
    async updatePortrait(id: number, params: MateTraitPortraitParams) {
      const res = await api.updateOneMateTraitPortrait(id, params);
      return res.item;
    },
    getOrFetchPortraitList: async function (
      mateTraitId?: number,
      perPage = PER_SIZE,
      mateId?: number
    ) {
      if (mateTraitId) this.portraitList = [];
      const page = Math.floor(this.portraitList.length / perPage) + 1;
      const toast = showLoadingToast({
        message: i18n.global.t("Message.loading"),
        duration: 0,
      });
      const res = await api.readMateTraitPortraitPage({
        pagination: {
          page,
          perPage,
        },
        sort: {
          field: "dtUpdate",
          order: "desc",
        },
        filter: {
          mateTraitId,
          mateId,
        },
      });
      toast.close();
      for (const item of res.page.data) {
        if (this.portraitList.find((portrait) => portrait.id === item.id))
          continue;
        this.portraitList.push(item);
      }
      return res.page.data;
    },
    deletePortrait: async function (id: number) {
      await api.deleteOneMateTraitPortrait(id);
      this.portraitList = this.portraitList.filter(
        (portrait) => portrait.id !== id
      );
    },
  },
});
